"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";
import { looksLikeHex } from "@/lib/tool-kit/developer/encoding-detect";

type Direction = "auto" | "to-hex" | "to-text";
type Grouping = "spaced" | "continuous" | "prefixed";

const HEX_STRIP_REGEX = /0x|[\s,]+/g;
const HEX_DIGITS_REGEX = /^[0-9a-fA-F]*$/;
const HEX_PAIR_REGEX = /.{2}/g;

function formatHexBytes(bytes: Uint8Array, grouping: Grouping): string {
  const hex: string[] = [];
  for (const byte of bytes) {
    hex.push(byte.toString(16).padStart(2, "0"));
  }
  if (grouping === "continuous") {
    return hex.join("");
  }
  if (grouping === "prefixed") {
    return hex.map((h) => `0x${h}`).join(" ");
  }
  return hex.join(" ");
}

function encodeToHex(
  text: string,
  grouping: Grouping
): { output: string; bytes: number; error: string } {
  const encoded = new TextEncoder().encode(text);
  return {
    output: formatHexBytes(encoded, grouping),
    bytes: encoded.length,
    error: "",
  };
}

function decodeFromHex(
  hex: string,
  strings: typeof HEX_CONVERTER_STRINGS
): {
  output: string;
  bytes: number;
  error: string;
} {
  const cleaned = hex.replace(HEX_STRIP_REGEX, "");
  if (!HEX_DIGITS_REGEX.test(cleaned)) {
    return {
      output: "",
      bytes: 0,
      error: strings.notHexDigitsError,
    };
  }
  if (cleaned.length % 2 !== 0) {
    return {
      output: "",
      bytes: 0,
      error: strings.oddDigitsError,
    };
  }
  const pairs = cleaned.match(HEX_PAIR_REGEX) ?? [];
  const byteValues = pairs.map((pair) => Number.parseInt(pair, 16));
  const output = new TextDecoder().decode(new Uint8Array(byteValues));
  return { output, bytes: byteValues.length, error: "" };
}

function convert(
  input: string,
  direction: Direction,
  grouping: Grouping,
  strings: typeof HEX_CONVERTER_STRINGS
): { output: string; bytes: number; error: string; decoded: boolean } {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { output: "", bytes: 0, error: "", decoded: false };
  }
  const shouldDecode =
    direction === "to-text" || (direction === "auto" && looksLikeHex(trimmed));
  if (shouldDecode) {
    return { ...decodeFromHex(trimmed, strings), decoded: true };
  }
  return { ...encodeToHex(input, grouping), decoded: false };
}

/** English strings. A translated content file overrides these through `ui`. */
export const HEX_CONVERTER_STRINGS = {
  autoDetect: "Auto-detect",
  textToHex: "Text → Hex",
  hexToText: "Hex → Text",
  spacedLabel: "Spaced (48 65)",
  continuousLabel: "Continuous (4865)",
  prefixedLabel: "0x-prefixed (0x48 0x65)",
  notHexDigitsError:
    "Input contains characters that are not hex digits (0-9, a-f). Remove them and try again.",
  oddDigitsError:
    "Hex input has an odd number of digits — each byte needs exactly two. Check for a missing digit.",
  direction: "Direction",
  hexOutputGrouping: "Hex output grouping",
  textOrHex: "Text or hex",
  inputPlaceholder: "Hi  —or—  48 65 6c 6c 6f",
  decodedText: "Decoded text",
  hexadecimal: "Hexadecimal",
  bytesCount: "one:{count} byte|other:{count} bytes",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
} as const;

export default function HexConverterWidget() {
  const [input, setInput] = useState("");
  const [direction, setDirection] = useState<Direction>("auto");
  const [grouping, setGrouping] = useState<Grouping>("spaced");
  const [copied, setCopied] = useState(false);

  const locale = useToolLocale();
  const strings = useToolUi(HEX_CONVERTER_STRINGS);
  const fmt = useToolNumber();

  const directions: { value: Direction; label: string }[] = [
    { value: "auto", label: strings.autoDetect },
    { value: "to-hex", label: strings.textToHex },
    { value: "to-text", label: strings.hexToText },
  ];

  const groupings: { value: Grouping; label: string }[] = [
    { value: "spaced", label: strings.spacedLabel },
    { value: "continuous", label: strings.continuousLabel },
    { value: "prefixed", label: strings.prefixedLabel },
  ];

  const { output, bytes, error, decoded } = convert(
    input,
    direction,
    grouping,
    strings
  );

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  return (
    <>
      {/* Direction */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.direction}
        </p>
        <div className="flex flex-wrap gap-3">
          {directions.map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d.value}
            >
              <input
                checked={direction === d.value}
                className="accent-primary"
                name="direction"
                onChange={() => setDirection(d.value)}
                type="radio"
                value={d.value}
              />
              {d.label}
            </label>
          ))}
        </div>
      </section>

      {/* Grouping */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.hexOutputGrouping}
        </p>
        <div className="flex flex-wrap gap-3">
          {groupings.map((g) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={g.value}
            >
              <input
                checked={grouping === g.value}
                className="accent-primary"
                name="grouping"
                onChange={() => setGrouping(g.value)}
                type="radio"
                value={g.value}
              />
              {g.label}
            </label>
          ))}
        </div>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="hex-input"
        >
          {strings.textOrHex}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="hex-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Error */}
      {error.length > 0 && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
          {error}
        </p>
      )}

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="hex-output"
          >
            {decoded ? strings.decodedText : strings.hexadecimal}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="hex-output"
            readOnly
            value={output}
          />
          <p className="text-muted-foreground text-xs">
            {tPlural(strings.bytesCount, locale, bytes, { count: fmt(bytes) })}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
    </>
  );
}
